/**
 * config.ts – Single Source für die App-Konfiguration.
 *
 * Hier stehen Titel (appName), Logo (logoUrl, logoColor),
 * Sidebar-Struktur, Breakpoints und Growl-Defaults. Alle Konsumenten importieren
 * appConfig von hier.
 *
 * Zusammenhänge:
 *   - types.ts              → AppConfig, Breakpoints, GrowlConfig
 *   - App.tsx               → Layout, Sidebar, Favicon-Setup
 *   - LoginPage.tsx         → Logo und Titel auf der Login-Seite
 *   - Sidebar.tsx, TopNav   → Logo, Sidebar-Struktur, Breakpoints
 *   - Growl.tsx             → Growl-Konfiguration (Position, Auto-Dismiss)
 *
 * appName ändern? Hier anpassen und zusätzlich <title> in index.html setzen (Tab-Titel vor App-Load).
 */
import type { AppConfig, Breakpoints, GrowlConfig } from '@/types';

/** Standard-Breakpoints (px). Schwellen für responsive Layouts: mobile < tablet < desktop < wide. */
export const defaultBreakpoints: Breakpoints = {
  tablet: 768,
  desktop: 1024,
  wide: 1280,
  paginationTextFrom: 768,
  tableStickyLeftFrom: 768,
  viewportRightSideRatio: 0.7,
};

/** Standard-Growl/Toast-Konfiguration: Position, Breite, Auto-Dismiss-Dauer und Abstand. */
export const defaultGrowlConfig: GrowlConfig = {
  top: 56,
  right: 16,
  maxWidth: 384,
  autoDismissMs: 4000,
  gap: 8,
};

/** Vollständige App-Standardkonfiguration. Single Source für Branding und Struktur. */
export const defaultConfig: AppConfig = {
  initialSidebarMode: 'expanded',
  defaultSidebarWidth: 256,
  maxSidebarWidth: 400,
  topNavHeight: 64,
  growl: { ...defaultGrowlConfig },
  breakpoints: { ...defaultBreakpoints },
  sidebarAccordion: true,
  sidebarName: 'Xeno',
  appName: 'Xeno Admin',
  logoUrl: './images/logo.svg',
  logoColor: '#2563eb',
  sidebar: [
    {
      label: 'Menu',
      children: [
        { label: 'Schreibtisch', icon: 'gauge', href: '/' },
        {
          label: 'Betrieb',
          icon: 'clipboard-check',
          children: [
            { label: 'Aufgaben', href: '/tasks' },
            { label: 'Check-in', href: '/checkins' },
            { label: 'Küchenliste', href: '/kitchen' },
            { label: 'Schlüsselvergabe', href: '/occupancy/key-handouts' },
          ],
        },
        {
          label: 'Kontakte',
          icon: 'contact',
          children: [
            { label: 'Personen', href: '/persons' },
            { label: 'Organisationen', href: '/organizations' },
          ],
        },
        { label: 'Termine', icon: 'calendar-days', href: '/dates' },
        { label: 'Raumbelegung', icon: 'layout-grid', href: '/occupancy/rooms' },
        { label: 'Anmelder', icon: 'clipboard-list', href: '/signupers' },
        {
          label: 'Kommunikation',
          icon: 'mail',
          children: [
            { label: 'Posteingang', href: '/communications/inbox' },
            { label: 'Abonnenten', href: '/communications/mailing' },
          ],
        },
      ],
    },
    {
      label: 'Verwaltung',
      children: [
        { label: 'Dateien', icon: 'folder-open', href: '/files' },
        {
          label: 'Inhalte',
          icon: 'file-text',
          children: [
            {
              label: 'Beiträge',
              children: [
                { label: 'Alle Beiträge', href: '/posts' },
                { label: 'Kategorien', href: '/settings/post-categories' },
              ],
            },
            { label: 'Websites', href: '/websites' },
            { label: 'Formulare', href: '/forms' },
          ],
        },
        {
          label: 'Finanzen',
          icon: 'receipt',
          children: [
            { label: 'Rechnungen', href: '/invoices' },
          ],
        },
        {
          label: 'Einstellungen',
          icon: 'settings',
          children: [
            { label: 'Allgemein', href: '/settings/general' },
            { label: 'Systembenutzer', href: '/settings/systemuser' },
            { label: 'Mandanten', href: '/settings/client' },
            { label: 'Rechte & Rollen', href: '/settings/roles' },
            {
              label: 'AI',
              children: [
                { label: 'AI-Verbindungen', href: '/settings/ai' },
                { label: 'AI-Aufgaben', href: '/settings/ai-tasks' },
              ],
            },
            { label: 'E-Mail-Konten', href: '/settings/emailaccounts' },
            {
              label: 'Benachrichtigungen',
              children: [
                { label: 'Vorlagen', href: '/settings/notificationtemplates' },
                { label: 'Designs', href: '/settings/notificationdesigns' },
              ],
            },
            {
              label: 'Buchhaltung',
              children: [
                { label: 'Konten', href: '/settings/accounting/accounts' },
                { label: 'Steuersätze', href: '/settings/accounting/taxes' },
                { label: 'Zahlungsmethoden', href: '/settings/accounting/payment-methods' },
                { label: 'Zahlungsbedingungen', href: '/settings/payment-terms' },
                { label: 'Stornobedingungen', href: '/settings/cancellation-policies' },
              ],
            },
            {
              label: 'Preise',
              children: [
                { label: 'Preise', href: '/settings/prices' },
                { label: 'Preistypen', href: '/settings/pricetypes' },
              ],
            },
            {
              label: 'Orte & Räume',
              children: [
                { label: 'Orte & Räume', href: '/settings/locrooms' },
                { label: 'Trakte & Etagen', href: '/settings/tractsfloors' },
                { label: 'Schlüsselstamm', href: '/settings/locrooms/keys' },
                { label: 'Reinigungsprofile', href: '/settings/cleaningprofiles' },
                { label: 'Raumtypen', href: '/settings/roomtypes' },
              ],
            },
            {
              label: 'Termine',
              children: [
                { label: 'Kalender', href: '/settings/calendars' },
                { label: 'Termintypen', href: '/settings/datetypes' },
                { label: 'Terminstati', href: '/settings/datestatuses' },
                { label: 'Terminkatalog', href: '/settings/datecatalog' },
                { label: 'Terminkategorien', href: '/settings/date-categories' },
              ],
            },
            {
              label: 'Anmeldungen',
              children: [
                { label: 'Anmeldetypen', href: '/settings/signupertypes' },
              ],
            },
            {
              label: 'Aufgaben',
              children: [
                { label: 'Aufgabenvorlagen', href: '/settings/task-templates' },
              ],
            },
            {
              label: 'Kontakte',
              children: [
                { label: 'Kontakttypen', href: '/settings/contact-types' },
                { label: 'Ansprachen', href: '/settings/salutations' },
                { label: 'Verteilertypen', href: '/settings/mailing-types' },
              ],
            },
            {
              label: 'Inhalte',
              children: [
                { label: 'Beitragstypen', href: '/settings/posttypes' },
              ],
            },
            {
              label: 'Ernährung',
              children: [
                { label: 'Ernährungsprofile', href: '/settings/dietaryprofiles' },
                { label: 'Allergien & Unverträgl.', href: '/settings/allergies' },
              ],
            },
            {
              label: 'Erweitert',
              children: [
                { label: 'Feldsteuerung', href: '/settings/fieldcontrols' },
                { label: 'Hilfe-Verwaltung', href: '/settings/help' },
              ],
            },
          ],
        },
      ],
    },
  ],
  sidebarFooter: [
    { label: 'Logout', action: 'logout' },
  ],
};

/** Von allen Konsumenten zu verwendende App-Konfiguration (Single Source: diese Datei). */
export const appConfig: AppConfig = defaultConfig;

// ═══════════════════════════════════════════════════════════════════════════════
// Visuelle Identitaet: Gender + Organisation – Fallback-Defaults
// ═══════════════════════════════════════════════════════════════════════════════
//
// Keys = DB-Werte (system_customers.gender / system_dates_signupers.gender TINYINT).
// Werden per deriveColorSet() in Rahmen-, Hintergrund-, Text- und Badge-Farben abgeleitet.
//
// Spaeter (Phase 2) koennen diese Werte durch eine DB-Entitaet (system_contacttypes)
// ueberschrieben werden. resolveGenderConfig() akzeptiert dafuer einen optionalen
// configMap-Parameter; ohne configMap greifen diese Fallbacks.
//
// Verwendet von: ItemChip (Rahmen, Badges, SyncBar), StatsBar, DateHeader,
// BookingCounter, GanttView, GuiElements (Demo-Karten).
// ═══════════════════════════════════════════════════════════════════════════════

import type { SpecialTypeConfig } from '@/types';

/**
 * Fallback-Config pro Gender-DB-Wert (0=Maennlich, 1=Weiblich, 2=Divers).
 * Literal-Zahlen als Keys – kein Import von Gender-Enum noetig, kein Import-Zyklus.
 */
export const GENDER_DEFAULTS: Record<number, SpecialTypeConfig> = {
  0: { label: 'Männlich', color: '#3b82f6', icon: 'mars' },
  1: { label: 'Weiblich', color: '#ec4899', icon: 'venus' },
  2: { label: 'Divers',   color: '#a855f7', icon: 'non-binary' },
};

/** Neutraler Fallback fuer null/unbekanntes Gender (circle-dot als stilistisch neutrales Symbol). */
export const GENDER_DEFAULT_FALLBACK: SpecialTypeConfig = { label: 'Unbekannt', color: '#94a3b8', icon: 'circle-dot' };

/** Visuelle Identitaet fuer Organisations-Kontakte (system_customers.type=1). */
export const ORGANISATION_DEFAULT: SpecialTypeConfig = { label: 'Organisation', color: '#f59e0b', icon: 'building' };

// ═══════════════════════════════════════════════════════════════════════════════
// Visuelle Identitaet: Zimmerpräferenz – Fallback-Defaults
// ═══════════════════════════════════════════════════════════════════════════════
//
// Keys = DB-Werte (system_dates_signupers.preference TINYINT).
// Werden per deriveColorSet() in Rahmen-, Hintergrund-, Text- und Badge-Farben abgeleitet.
//
// Verwendet von: ItemChip (Badge), Sidebar (Filter), AutoAssign, AddSignuperPopup,
// StatsBar, TimeOptionsPopup.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Fallback-Config pro Preference-DB-Wert (0=Egal, 1=Einzelzimmer, 2=Doppelzimmer, 3=Mehrbettzimmer).
 * Kurzlabel (shortLabel) wird in kompakten Badges angezeigt (EZ/DZ/MBZ/Egal).
 */
export const ROOM_PREFERENCE_DEFAULTS: Record<number, SpecialTypeConfig & { shortLabel: string }> = {
  0: { label: 'Egal',            shortLabel: 'Egal', color: '#94a3b8', icon: 'bed' },
  1: { label: 'Einzelzimmer',    shortLabel: 'EZ',   color: '#3b82f6', icon: 'bed-single' },
  2: { label: 'Doppelzimmer',    shortLabel: 'DZ',   color: '#f59e0b', icon: 'bed-double' },
  3: { label: 'Mehrbettzimmer',  shortLabel: 'MBZ',  color: '#10b981', icon: 'users' },
};

// ═══════════════════════════════════════════════════════════════════════════════
// Visuelle Identitaet: Altersgruppe – Fallback-Defaults
// ═══════════════════════════════════════════════════════════════════════════════
//
// Keys = DB-Werte (system_dates_signupers.age_group TINYINT).
// Werden per deriveColorSet() in Rahmen-, Hintergrund-, Text- und Badge-Farben abgeleitet.
//
// Verwendet von: ItemChip (Badge), Sidebar (Filter), AddSignuperPopup, GuiElements.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Fallback-Config pro Altersgruppe-DB-Wert (0=Kind, 1=Jugendlich, 2=Erwachsen).
 * Kurzlabel (shortLabel) wird in kompakten Badges angezeigt (K/J/E).
 */
export const AGE_GROUP_DEFAULTS: Record<number, SpecialTypeConfig & { shortLabel: string }> = {
  0: { label: 'Kind',         shortLabel: 'K', color: '#10b981', icon: 'baby' },
  1: { label: 'Jugendlich',   shortLabel: 'J', color: '#f59e0b', icon: 'graduation-cap' },
  2: { label: 'Erwachsen',    shortLabel: 'E', color: '#6366f1', icon: 'user' },
};

// ═══════════════════════════════════════════════════════════════════════════════
// SyncBar – Farben für Zeit-Balken (EventPlanner)
// ═══════════════════════════════════════════════════════════════════════════════
//
// Zwei Farbsätze: normal = SyncBar auf Karten, header = SyncBar im Zeitblock-Kopf (embedded).
// Verwendet von: EventPlanner/cards/SyncBar.tsx.
//
// ═══════════════════════════════════════════════════════════════════════════════

export type SyncBarColorSet = {
  /** Hintergrund des Balken-Tracks (grauer Balken). */
  trackBackground: string;
  segmentFallback: string;
  segmentOwnTimes: string;
  segmentException: string;
  markerArrivalDeparture: string;
  gapLine: string;
};

/** Farben für die normale SyncBar (Karten: Person, Preis, Extra). */
export const SYNC_BAR_COLORS: SyncBarColorSet = {
  trackBackground: 'rgba(243, 244, 246, 0)',
  segmentFallback: 'rgb(148, 163, 184)',
  segmentOwnTimes: 'rgb(34, 197, 94)',
  segmentException: 'rgb(239, 68, 68)',
  markerArrivalDeparture: '#000',
  gapLine: '#000',
};

/** Farben für die SyncBar im Zeitblock-Header (embedded). */
export const SYNC_BAR_HEADER_COLORS: SyncBarColorSet = {
  trackBackground: 'rgba(243, 244, 246, 0.8)',
  segmentFallback: 'rgb(148, 163, 184)',
  segmentOwnTimes: 'rgb(34, 197, 94)',
  segmentException: 'rgb(239, 68, 68)',
  markerArrivalDeparture: '#000',
  gapLine: 'rgba(0,0,0,0)',
};
